/**
 * Settings -> Phone -> AI answering ("AI-vastaja"): an AI that picks up the
 * phone itself.
 *
 * Deliberately not the co-pilot's page, and deliberately not named like it.
 * The co-pilot listens to a call a person is having; this one IS the other end
 * of the call, so every switch on this page decides what a stranger hears.
 *
 * Three cards, in the order somebody works through them: the settings with
 * the server's verdict on whether the AI would really answer, the knowledge
 * text it answers from, and the calls it has taken - which is where the owner
 * finds out whether the first two were right. The settings save with one
 * button, like the co-pilot's. The knowledge text has its own, because it is a
 * document edited over days rather than a switch.
 */
import { useEffect, useState, type FormEvent } from "react";
import { Bot, RefreshCw, Save } from "lucide-react";
import { useT } from "../../../../../i18n/I18nProvider";
import { getTelephonySettings, saveTelephonySettings, type TelephonyStatus } from "../../../../../lib/calls";
import { formatPhone } from "../../../../../lib/phone";
import { Btn, StatusPill } from "../../../../ui";
import { PopHost } from "../../../../shell/Pop";
import { ConnStatus, IntegrationCard, PanelLoader } from "../../panel/IntegrationCard";
import { Note, SaveRow, ToggleRow } from "../../panel/fields";
import { AiAgentFields, toAgentDraft, toAgentPatch, type AgentDraft } from "./AiAgentFields";
import { KnowledgeEditor } from "./KnowledgeEditor";
import { RecentAgentCalls } from "./RecentAgentCalls";
import { agentBlockerNotes } from "./aiAgentNotes";

export function AiAgentCard() {
  const t = useT();
  const [status, setStatus] = useState<TelephonyStatus | null>(null);
  const [draft, setDraft] = useState<AgentDraft | null>(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");

  function hydrate(data: TelephonyStatus) {
    setStatus(data);
    setDraft(toAgentDraft(data.aiAgent));
  }

  useEffect(() => {
    getTelephonySettings()
      .then(hydrate)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!draft) return;
    setSaveState("saving");
    try {
      // Only this group. The row also holds the Twilio keys and every other
      // tab's settings, and the server keeps whatever a patch leaves out.
      hydrate(await saveTelephonySettings({ aiAgent: toAgentPatch(draft) }));
      setSaveState("saved");
      setTimeout(() => setSaveState("idle"), 2500);
    } catch {
      setSaveState("error");
    }
  }

  if (loading) return <PanelLoader />;

  const agent = status?.aiAgent;
  // What the server is running on, not what is being edited: an unsaved tick
  // must not make a gap look fixed.
  const notes = agentBlockerNotes(agent?.blockers);

  return (
    // The voice and model fields are `Pop` menus and Settings mounts no
    // `PopHost` of its own - a `Pop` without one is a control that silently
    // never opens. Every card here that owns a menu carries its own, so the
    // one open at a time is also the one on this card.
    <PopHost>
      <div className="space-y-4">
        <IntegrationCard
          icon={Bot}
          title={t("settings.aiAgent.title")}
          desc={t("settings.aiAgent.desc")}
          status={
            <ConnStatus
              ok={agent?.ready ?? false}
              okLabel={t("settings.aiAgent.statusOn")}
              offLabel={t("settings.aiAgent.statusOff")}
            />
          }
        >
          {draft && agent ? (
            <form onSubmit={handleSave} className="space-y-5">
              <ToggleRow
                label={t("settings.aiAgent.enable")}
                hint={t("settings.aiAgent.enableHint")}
                checked={draft.enabled}
                onChange={(enabled) => setDraft({ ...draft, enabled })}
              />

              {/* Every gap at once, each naming where it is fixed - a switch
                  that says "on" to a phone that never answers is the failure
                  this list exists to prevent. */}
              {notes.map((note) => (
                <Note key={note.blocker} tone={note.tone}>
                  {t(`settings.aiAgent.blocker.${note.key}`, { code: note.blocker })}
                </Note>
              ))}

              {/* Who hears a machine right now, spelled out. The one thing on
                  this page nobody should have to work out from two fields. */}
              {agent.ready ? (
                <Note tone="ok">
                  {agent.testCallers.length > 0
                    ? t("settings.aiAgent.reachTest", { numbers: agent.testCallers.map(formatPhone).join(", ") })
                    : t("settings.aiAgent.reachModes", {
                        modes: agent.modes.map((m) => t(`settings.aiAgent.mode.${m}`)).join(", "),
                      })}
                </Note>
              ) : null}

              <Note tone="info">
                <strong className="font-[650]">{t("settings.aiAgent.prototypeTitle")}</strong>{" "}
                {t("settings.aiAgent.prototype")}
              </Note>

              <AiAgentFields draft={draft} onChange={setDraft} />

              <SaveRow>
                <Btn type="submit" tone="dark" disabled={saveState === "saving"}>
                  {saveState === "saving" ? (
                    <RefreshCw className="size-3.5 animate-spin" />
                  ) : (
                    <Save className="size-3.5" />
                  )}
                  {saveState === "saving" ? t("settings.aiAgent.saving") : t("settings.aiAgent.save")}
                </Btn>
                {saveState === "saved" ? <StatusPill tone="ok">{t("settings.aiAgent.saved")}</StatusPill> : null}
                {saveState === "error" ? <StatusPill tone="lt">{t("settings.aiAgent.saveError")}</StatusPill> : null}
              </SaveRow>
            </form>
          ) : (
            // No form over a status that never arrived: saving defaults on top
            // of a row nobody has seen would switch things on or off blind.
            <Note tone="bad">{t("settings.aiAgent.loadError")}</Note>
          )}
        </IntegrationCard>

        <KnowledgeEditor />
        <RecentAgentCalls />
      </div>
    </PopHost>
  );
}
